/**
 * Business Settings Store
 * 
 * Shared Zustand store for the active business's settings AND resolved features.
 * Uses the FeatureGate-powered /features endpoint for authoritative feature checks.
 * Used by Sidebar (nav item hiding) and UpgradeGate (route guards).
 */
import { create } from 'zustand';
import { businessesApi } from './api';
import { useAuthStore } from './authStore';

export const useBusinessSettings = create((set, get) => ({
    settings: null,
    features: null,   // Resolved features from FeatureGate (snake_case keys)
    tier: null,        // Resolved tier name (essentials, ai_employee, growth)
    businessId: null,
    loading: false,
    error: null,
    _lastFetchKey: null,

    /**
     * Fetch settings + resolved features for the currently active business.
     * @param {boolean} force - Force a fresh fetch even if cached
     */
    fetchSettings: async (force = false) => {
        const { isAgencyAdmin, user, token } = useAuthStore.getState();

        const urlParams = new URLSearchParams(window.location.search);
        const businessId = urlParams.get('business_id') || user?.business_id;

        if (isAgencyAdmin() && !businessId) {
            set({ settings: null, features: null, tier: null, businessId: null, loading: false });
            return null;
        }

        const fetchKey = `${businessId}_${token}`;
        if (!force && get()._lastFetchKey === fetchKey && get().settings !== null) {
            return get().settings;
        }

        if (!businessId || !token) {
            set({ settings: null, features: null, tier: null, businessId: null, loading: false });
            return null;
        }

        set({ loading: true, error: null });

        try {
            // Fetch settings and resolved features in parallel
            const [biz, featuresData] = await Promise.all([
                businessesApi.getOne(businessId),
                businessesApi.getFeatures(businessId).catch(() => null)
            ]);

            const settings = biz.settings || {};
            const features = featuresData?.features || {};
            const tier = featuresData?.tier || null;

            set({
                settings,
                features,
                tier,
                businessId: parseInt(businessId),
                loading: false,
                _lastFetchKey: fetchKey
            });
            return settings;
        } catch (err) {
            console.error('[BusinessSettings] Failed to fetch:', err);
            set({ settings: null, features: null, tier: null, loading: false, error: err.message });
            return null;
        }
    },

    /**
     * Check if a specific feature is enabled (uses FeatureGate-resolved features).
     * @param {string} featureKey - snake_case key matching featureRegistry.js (e.g. 'pipeline', 'stripe_payments')
     * @returns {boolean}
     */
    hasFeature: (featureKey) => {
        const { features } = get();
        if (!features) return false;
        return !!features[featureKey];
    },

    /**
     * Clear the cached settings (e.g. on logout or business switch)
     */
    clear: () => {
        set({ settings: null, features: null, tier: null, businessId: null, loading: false, error: null, _lastFetchKey: null });
    }
}));
